import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  FolderOpen,
  Search,
  Utensils,
} from "lucide-react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import { categoriesGetAll, itemsGetAll } from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "~/components/ui/input-group";

interface RoutingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  routingMode: "categories" | "items";
  categoriesSelected: string[];
  setCategoriesSelected: (ids: string[]) => void;
  itemsSelected: string[];
  setItemsSelected: (ids: string[]) => void;
  stationName: string;
  color: { color: string; stroke: string; name: string };
}

export function RoutingDialog({
  isOpen,
  onClose,
  routingMode,
  categoriesSelected,
  setCategoriesSelected,
  itemsSelected,
  setItemsSelected,
  stationName,
  color,
}: RoutingDialogProps) {
  const [searchValue, setSearchValue] = useState("");
  const [localCategories, setLocalCategories] =
    useState<string[]>(categoriesSelected);
  const [localItems, setLocalItems] = useState<string[]>(itemsSelected);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalCategories(categoriesSelected);
      setLocalItems(itemsSelected);
      setSearchValue("");
    }
    if (!open) {
      onClose();
    }
  };

  const { data: categoriesResult } = useQuery({
    ...convexQuery(categoriesGetAll, {
      includeItemsCount: true,
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "categories",
  });

  const { data: itemsResult } = useQuery({
    ...convexQuery(itemsGetAll, {
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "items",
  });

  const categories = categoriesResult?.page ?? [];
  const items = itemsResult?.page ?? [];

  const toggleSelection = (id: string) => {
    if (routingMode === "categories") {
      setLocalCategories((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    } else {
      setLocalItems((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
  };

  const handleSave = () => {
    if (routingMode === "categories") {
      setCategoriesSelected(localCategories);
    } else {
      setItemsSelected(localItems);
    }
    onClose();
  };

  const selectedCount =
    routingMode === "categories" ? localCategories.length : localItems.length;

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <DialogContent className="sm:max-w-lg" noScrollPrevention>
        <DialogTitle className="sr-only">
          Select {routingMode === "categories" ? "Categories" : "Items"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Choose which {routingMode} this station will handle
        </DialogDescription>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-center">
            <h2 className="h4">
              Select {routingMode === "categories" ? "Categories" : "Items"}
            </h2>
            <p className="text-muted-foreground">
              Choose which {routingMode} this station will handle
            </p>
          </div>

          <div className="flex justify-center">
            <div
              className="flex h-10 w-36 items-center justify-center rounded-lg text-sm font-medium"
              style={{ background: color.color, stroke: color.stroke }}
            >
              {stationName}
            </div>
          </div>

          <InputGroup>
            <InputGroupAddon align="inline-start">
              <Search className="size-4" />
            </InputGroupAddon>
            <InputGroupInput
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={`Search ${routingMode}...`}
              value={searchValue}
            />
          </InputGroup>

          <div className="max-h-80 overflow-y-auto">
            {routingMode === "categories" ? (
              <div className="flex flex-col gap-2">
                {categories
                  .filter((cat) =>
                    cat.name.toLowerCase().includes(searchValue.toLowerCase())
                  )
                  .map((category) => (
                    <button
                      className={twJoin(
                        "flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left",
                        localCategories.includes(category._id)
                          ? "bg-accent"
                          : "hover:bg-accent/50"
                      )}
                      key={category._id}
                      onClick={() => toggleSelection(category._id)}
                      type="button"
                    >
                      <FolderOpen className="text-category h-5 w-5" />
                      <div className="flex-1">
                        <p className="font-medium">{category.name}</p>
                        <p className="text-muted-foreground text-sm">
                          {category.itemCount} items
                        </p>
                      </div>
                      {localCategories.includes(category._id) && (
                        <CheckCircle2 className="text-primary h-5 w-5" />
                      )}
                    </button>
                  ))}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {items
                  .filter((item) =>
                    item.name.toLowerCase().includes(searchValue.toLowerCase())
                  )
                  .map((item) => (
                    <button
                      className={twJoin(
                        "flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left",
                        localItems.includes(item._id)
                          ? "bg-accent"
                          : "hover:bg-accent/50"
                      )}
                      key={item._id}
                      onClick={() => toggleSelection(item._id)}
                      type="button"
                    >
                      <Utensils className="text-item h-5 w-5" />
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                      </div>
                      {localItems.includes(item._id) && (
                        <CheckCircle2 className="text-primary h-5 w-5" />
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <p className="text-muted-foreground text-center text-sm">
            {selectedCount} selected
          </p>

          <div className="flex justify-between">
            <Button onClick={onClose} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Save Selection
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
